import {acceptHMRUpdate, defineStore} from "pinia"
import type {AccountInfo, AuthState, Payload, Role, Roles, UserProfileInfo} from "@/types/auth.d"
import _castArray from "lodash/castArray"
import {http} from "@/utils/http"
import {Logger} from "@/utils"
import {Capacitor} from '@capacitor/core'
import {useStoreI18n} from "@/composables/useStoreI18n";

export const useAuthStore = defineStore("auth", {

    state: (): AuthState => ({
        token: '',
        logged: false,
        user: {} as Record<string, any>,
        account: {} as AccountInfo,
        pushToken: '',
        pushTokenSaved: false,
        webPushEndpoint: ''
    }),
    actions: {
        setLogged(payload: Payload) {
            this.token = payload.token
            this.logged = true
            this.user = payload.tokenData
            this.savePushToken()
        },
        setPushToken(token: string) {
            this.pushToken = token
            this.savePushToken()
        },
        setLogout() {
            this.token = ''
            this.logged = false
            this.user = {}
            this.account = {} as AccountInfo
        },
        getMissingSubscriptions() {
            return findMissingSubscriptions(this)
        },
        getSubscriptionIsRequired() {
            return findMissingSubscriptions(this).length > 0
        },
        getAccountRolePlayerFree() {
            return accountRoleIsPlayerFree(this)
        },
        getAccountRolePlayerPro() {
            return accountRoleIsPlayerPro(this)
        },
        getAccountRoleClub() {
            return accountRoleIsClub(this)
        },
        getAccountRoleManager() {
            return accountRoleIsManager(this)
        },
        getAccountRoleTrainer() {
            return accountRoleIsTrainer(this)
        },
        getUpgradeButtonLabel() {
            if (accountRoleIsPlayerFree(this)) {
                return "Upgrade now!"
            } else if (findMissingSubscriptions(this).length > 0) {
                return "Upgrade now!"
            } else {
                return "No subscription required"
            }
        },
        async fetchAccountInfo() {
            if (this.token === undefined || this.token === '')
                return

            try {
                const data = await http.get(`${import.meta.env.VITE_API_URL}/user/account-info`)

                if (data.message) {
                    Logger.error(data.message)
                    return
                }

                this.account = data as AccountInfo

            } catch (error) {
                Logger.error('Error fetching account information', error)
            }
        },
        async fetchAccountInfoForUser(id: number) {
            if (this.token === undefined || this.token === '')
                return {} as UserProfileInfo

            try {
                const data = await http.get(`${import.meta.env.VITE_API_URL}/user/profile/${id}`)

                if (data.message) {
                    Logger.error(data.message)
                    return {} as UserProfileInfo
                }

                return data as UserProfileInfo

            } catch (error) {
                Logger.error('Error fetching account information', error)
                return {} as UserProfileInfo
            }
        },
        fetchAccountInfoForUserAsync(id: number) {
            return new Promise(async (resolve, reject) => {
                if (this.token === undefined || this.token === '') {
                    reject("No token")
                    return
                }

                try {
                    const data = await http.get(`${import.meta.env.VITE_API_URL}/user/profile/${id}`)

                    if (data.message) {
                        reject(t(data.message))
                    } else {
                        resolve(data as UserProfileInfo)
                    }
                } catch (error) {
                    reject(error)
                }
            })
        },
        async fetchPaymentUrl(role: string): Promise<string> {
            try {
                const data = await http.get(`${import.meta.env.VITE_API_URL}/subscription/payment-url/${role}`)

                if (data.error) {
                    Logger.error(data.error)
                    return ""
                }

                return data.paymentUrl ?? ""

            } catch (error) {
                Logger.error('Error fetching payment url', error)
                return ""
            }
        },
        async requestPasswordReset(email: string): Promise<any> {
            if (!email || email === '')
                Logger.error('No email provided')

            try {
                const data = await http.post(`${import.meta.env.VITE_API_URL}/reset-password/request`, {
                    body: {
                        email: email
                    }
                })

                return data

            } catch (error) {
                Logger.error(error)
                return error
            }
        },
        async savePushToken(): Promise<boolean> {
            try {
                const deviceType = Capacitor.getPlatform()
                const token = this.pushToken

                Logger.debug('Call Saving push token:', token, 'for device type:', deviceType, `${import.meta.env.VITE_API_URL}/user/save-push-token`)

                if (this.pushTokenSaved) return true;
                if (token.length < 10) return false;

                Logger.debug('Saving push token:', token, 'for device type:', deviceType, `${import.meta.env.VITE_API_URL}/user/save-push-token`)

                const result = await http.post(`${import.meta.env.VITE_API_URL}/user/save-push-token`, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: {
                        token: token,
                        deviceType: deviceType
                    }
                })

                if (result.success) {
                    Logger.debug('Push token saved successfully:', result.success)
                    this.pushTokenSaved = true
                    return true
                } else {
                    const {t} = useStoreI18n()
                    Logger.error('Failed to save push token:', t(result.message) || 'Unknown error')
                }
            } catch (error) {
                Logger.error('Error saving push token:', error)
            }

            return false
        }
    },
    getters: {
        isLogged(state) {
            return state.logged
        },
        userRole(state) {
            return getRoleFromRoles(state) ?? ""
        },
        userData(state) {
            return state.user
        },
        isRoleGranted(state) {
            return (roles?: Roles) => {
                if (!roles) {
                    return true
                }

                const arrRoles: Role[] = _castArray(roles)
                if (arrRoles.includes("all"))
                    return true

                const tokenUserRoles = getRoleFromRoles(state)
                if (!tokenUserRoles)
                    return false

                return arrRoles.includes(tokenUserRoles)
            }
        },
        isSubscriptionRequired(state) {
            return findMissingSubscriptions(state).length > 0
        },
        getAccountInfo(state) {
            return state.account
        },
        isRolePlayerFree(state) {
            return accountRoleIsPlayerFree(state)
        },
        isRolePlayerPro(state) {
            return accountRoleIsPlayerPro(state)
        },
        isRolePlayer(state) {
            return accountRoleIsPlayerFree(state) || accountRoleIsPlayerPro(state)
        },
        isRoleManager(state) {
            return accountRoleIsManager(state)
        },
        isRoleClub(state) {
            return accountRoleIsClub(state)
        },
        isRoleTrainer(state) {
            return accountRoleIsTrainer(state)
        },
        upgradeButtonLabel(state) {
            if (accountRoleIsPlayerFree(state)) {
                return "Upgrade now!"
            } else if (findMissingSubscriptions(state).length > 0) {
                return "Upgrade now!"
            } else {
                return "No subscription required"
            }
        }
    },
    persist: {
        // omit: ["user"]
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}


function findMissingSubscriptions(state: AuthState) {
    const rolesWithSubscription = ["ROLE_PLAYER_PRO", "ROLE_TRAINER", "ROLE_MANAGER", "ROLE_CLUB"];

    if (!state.account || !state.account.roles || !state.account.requiredSubscriptionsState)
        return []

    return Object.entries(state.account.requiredSubscriptionsState).filter(([key, _value]: [string, boolean]) => {
        return rolesWithSubscription.includes(key) && !_value
    })
}

function accountRoleIsPlayerFree(state: AuthState): boolean {
    if (!state.account.roles)
        return false

    const values = Object.values(state.account.roles) as string[]

    return values.includes("ROLE_PLAYER_FREE")
        && !values.includes("ROLE_PLAYER_PRO")
        && !values.includes("ROLE_TRAINER")
        && !values.includes("ROLE_MANAGER")
        && !values.includes("ROLE_CLUB")
}

function accountRoleIsPlayerPro(state: AuthState): boolean {
    if (!state.account.roles)
        return false

    const values = Object.values(state.account.roles) as string[]

    return (values.includes("ROLE_PLAYER_FREE")
            || values.includes("ROLE_PLAYER_PRO"))
        && !values.includes("ROLE_TRAINER")
        && !values.includes("ROLE_MANAGER")
        && !values.includes("ROLE_CLUB")
}

function accountRoleIsManager(state: AuthState): boolean {
    if (!state.account.roles)
        return false

    const values = Object.values(state.account.roles) as string[]
    return values.includes("ROLE_MANAGER")
}


function accountRoleIsClub(state: AuthState): boolean {
    if (!state.account.roles)
        return false

    const values = Object.values(state.account.roles) as string[]
    return values.includes("ROLE_CLUB")
}


function accountRoleIsTrainer(state: AuthState): boolean {
    if (!state.account.roles)
        return false

    const values = Object.values(state.account.roles) as string[]
    return values.includes("ROLE_TRAINER")
}

function getRoleFromRoles(state: AuthState): string {
    let role = state.account && state.account.roles
        ? Object.values(state.account.roles)[0]
        : undefined

    if (!role) {
        role = state.user && state.user.roles && state.user.roles instanceof Object
            ? Object.values(state.user.roles)[0]
            : undefined
    }

    return role
}
